/**
 * MCID overlay layer — diagnostic visualization of per-MCID glyph bboxes.
 *
 *
 * Renders one marker per MCID on the rendered PDF page, color-coded by the
 * parent struct elem's role. Orphan MCIDs (no parent) render as bright-red
 * oversized squares. Per parent struct with ≥2 children, a dashed convex-
 * hull polygon encloses the child MCID centroids — divergence between the
 * hull and the official struct-overlay bbox is the at-a-glance signal of
 * MCID-vs-struct misalignment (the CS4134 bug class).
 *
 * Pure module: no global DOM lookups, no implicit state. Output is mutation
 * of the supplied container.
 *
 * @module mcid-overlay
 */
import { projectBbox } from "./page-projection.js";
// ─── Role-color palette (mirrors overlays.js category colors) ────────────────
/**
 * Map a PDF struct role to a marker color. Values are hue-matched (within
 * lightness tolerance) to the corresponding overlays.js category fill so
 * a sighted user reads the MCID dot color and the parent overlay box as
 * "the same thing." Orphan markers use a distinct pure-red so the eye
 * snaps to them without role-color collision.
 *
 * Roles outside the table fall through to the "other" gray.
 */
const ROLE_TO_MARKER_COLOR = {
    // Heading — orange-red
    H: "#cc4444", H1: "#cc4444", H2: "#cc4444", H3: "#cc4444",
    H4: "#cc4444", H5: "#cc4444", H6: "#cc4444",
    // Paragraph / Span — blue
    P: "#1e6eaf", BlockQuote: "#1e6eaf", Quote: "#1e6eaf", Span: "#1e6eaf",
    // Figure / Form / Formula — green
    Figure: "#288c5a", Form: "#288c5a", Formula: "#288c5a",
    // Table family — orange
    Table: "#b46e14", TR: "#b46e14", TH: "#b46e14", TD: "#b46e14",
    THead: "#b46e14", TBody: "#b46e14", TFoot: "#b46e14",
    // List family — purple
    L: "#783ca5", LI: "#783ca5", Lbl: "#783ca5", LBody: "#783ca5",
    TOC: "#783ca5", TOCI: "#783ca5",
    // Caption — teal
    Caption: "#468ca5",
    // Annotations — gold
    Note: "#a58214", Reference: "#a58214", Annot: "#a58214", Link: "#a58214",
};
const ORPHAN_COLOR = "#ff0000";
const OTHER_COLOR = "#888888";
/**
 * Shorten a struct id to a 4-character display hash for hover tooltips.
 *
 * Struct ids in the IR look like `S/0/3/12`; we want a stable 4-char prefix
 * that's distinct enough for hover-disambiguation but doesn't bloat the
 * tooltip. Drop the leading `S/` and take the first 4 visible chars.
 *
 * @param structId — IR struct identifier, e.g. `S/0/3/12`.
 * @returns 4-char hash, or empty string when no struct id.
 */
export function shortStructHash(structId) {
    if (!structId || typeof structId !== "string")
        return "";
    const stripped = structId.replace(/^S\//, "").replace(/\//g, "");
    return stripped.slice(0, 4);
}
/**
 * Compute the convex hull of a set of 2D points using Andrew's monotone
 * chain algorithm. Returns the hull vertices in counter-clockwise order
 * starting from the lowest-leftmost point.
 *
 * Degenerate cases: <3 input points → returns input as-is (a "hull" of a
 * line / point can't be drawn as a polygon).
 *
 * Coordinates are pixel-space (canvas px), matching the renderer's output.
 */
export function convexHull(pts) {
    if (!Array.isArray(pts))
        return [];
    const valid = pts.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y));
    if (valid.length < 3)
        return valid.slice();
    // Sort lex by x then y
    const sorted = valid.slice().sort((a, b) => (a.x - b.x) || (a.y - b.y));
    const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower = [];
    for (const p of sorted) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }
    const upper = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
        const p = sorted[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}
/**
 * Build an MCID → parent-struct-id lookup from the IR struct-tree.
 *
 * The C# editor IR carries `mcids` as a flat array of `{ page, mcid,
 * centroid, bbox }` records — but the parent linkage lives on the struct-
 * tree side (each struct elem has a `mcids: number[]` field per page).
 * We accept either shape:
 *   - `mcids` records carry an explicit `parentStructId` (preferred), OR
 *   - `structsById` is provided and we walk it to derive the linkage.
 *
 * Returns `Map<"page:mcid", structId>`. Used to color-code attached MCIDs
 * and identify orphans.
 *
 * @param mcids — flat MCID list from `state.ir.mcids`.
 * @param structsById — optional reverse lookup.
 */
export function buildMcidOwnerMap(mcids, structsById) {
    const out = new Map();
    if (!Array.isArray(mcids))
        return out;
    for (const m of mcids) {
        if (!m || typeof m.mcid !== "number" || typeof m.page !== "number")
            continue;
        if (m.parentStructId && typeof m.parentStructId === "string") {
            out.set(`${m.page}:${m.mcid}`, m.parentStructId);
        }
    }
    if (structsById && typeof structsById.forEach === "function") {
        structsById.forEach((node, structId) => {
            if (!node)
                return;
            const mcs = Array.isArray(node.mcids) ? node.mcids : null;
            const page = typeof node.page === "number" ? node.page : null;
            if (!mcs || page === null)
                return;
            for (const m of mcs) {
                if (typeof m !== "number")
                    continue;
                const key = `${page}:${m}`;
                if (!out.has(key))
                    out.set(key, structId);
            }
        });
    }
    return out;
}
/**
 * Render the MCID overlay into the supplied container. Pure DOM mutation;
 * appended elements get class `.editor-mcid-bbox` (attached) or
 * `.editor-mcid-bbox.is-orphan` (no parent struct). Hulls are rendered as
 * `.editor-mcid-hull` polygons.
 *
 * The container is NOT cleared by this function — the host clears it from
 * the per-page render path. (Callers also clear it on toggle-off.)
 *
 * Called from `editor.js:renderOverlays` / `renderOverlaysForPage` when
 * `state.mcidOverlayVisible` is true.
 *
 * @returns number of MCID markers rendered
 */
export function renderMcidOverlay(opts) {
    const { mcids, pageNum, container, projection, ownerMap = null, roleByStructId = null, structsById = null, } = opts ?? {};
    if (!container || !Array.isArray(mcids) || mcids.length === 0)
        return 0;
    // Degenerate / not-yet-painted frame (zero-determinant transform).
    if (!projection)
        return 0;
    const [_ma, _mb, _mc, _md] = projection.userSpaceToDevice;
    if (!(_ma * _md - _mb * _mc))
        return 0;
    const owners = ownerMap instanceof Map
        ? ownerMap
        : buildMcidOwnerMap(mcids, structsById);
    const roleLookup = (id) => {
        if (!id)
            return "";
        if (typeof roleByStructId === "function")
            return roleByStructId(id) ?? "";
        if (roleByStructId && typeof roleByStructId.get === "function")
            return roleByStructId.get(id) ?? "";
        return "";
    };
    // Page-filtered MCIDs only.
    const onPage = mcids.filter((m) => m?.page === pageNum);
    if (onPage.length === 0)
        return 0;
    // Group attached MCIDs by parent struct id so we can render one convex
    // hull per struct. Centroids accumulate in pixel-space for direct hull
    // input.
    const groupCentroids = new Map();
    let rendered = 0;
    for (const m of onPage) {
        const bbox = m.bbox;
        if (!bbox || !Number.isFinite(bbox.x) || !Number.isFinite(bbox.y)
            || !Number.isFinite(bbox.w) || !Number.isFinite(bbox.h)) {
            continue;
        }
        const rect = projectBbox([bbox.x, bbox.y, bbox.w, bbox.h], projection);
        const ownerKey = `${m.page}:${m.mcid}`;
        const parentId = owners.get(ownerKey) ?? null;
        const role = roleLookup(parentId);
        const isOrphan = !parentId;
        const color = isOrphan
            ? ORPHAN_COLOR
            : (ROLE_TO_MARKER_COLOR[role] ?? OTHER_COLOR);
        const dot = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        dot.className = isOrphan ? "editor-mcid-bbox is-orphan" : "editor-mcid-bbox";
        dot.dataset.mcid = String(m.mcid);
        dot.dataset.page = String(m.page);
        if (parentId)
            dot.dataset.parentStructId = parentId;
        dot.style.left = `${rect.x}px`;
        dot.style.top = `${rect.y}px`;
        dot.style.width = `${rect.w}px`;
        dot.style.height = `${rect.h}px`;
        dot.style.borderColor = color;
        dot.style.backgroundColor = withAlpha(color, isOrphan ? 0.18 : 0.32);
        const tooltipParts = [`MCID ${m.mcid}`];
        if (role)
            tooltipParts.push(role);
        if (parentId) {
            const hash = shortStructHash(parentId);
            if (hash)
                tooltipParts.push(hash);
        }
        else {
            tooltipParts.push("orphan");
        }
        dot.setAttribute("title", tooltipParts.join(" · "));
        container.appendChild(dot);
        rendered++;
        if (parentId) {
            // Centroid in pixel-space (origin = top-left, matching CSS positioning)
            const cx = (typeof m.centroid === "object" && m.centroid && Number.isFinite(m.centroid.x))
                ? m.centroid.x : (bbox.x + bbox.w / 2);
            const cy = (typeof m.centroid === "object" && m.centroid && Number.isFinite(m.centroid.y))
                ? m.centroid.y : (bbox.y + bbox.h / 2);
            const cRect = projectBbox([cx, cy, 0, 0], projection);
            const entry = groupCentroids.get(parentId) ?? { color, points: [] };
            entry.points.push({ x: cRect.x, y: cRect.y });
            groupCentroids.set(parentId, entry);
        }
    }
    // Convex-hull polygons (one per parent struct with ≥2 children). Render
    // as SVG <polygon> inside a single per-container <svg> so the polygons
    // don't disturb DOM-level pointer events. SVG sits ABOVE the dot layer
    // via z-index in CSS — the hull is the diagnostic, not the dots.
    if (groupCentroids.size > 0) {
        const svgNs = "http://www.w3.org/2000/svg";
        const svg = document.createElementNS(svgNs, "svg"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        svg.classList.add("editor-mcid-hull-svg");
        svg.setAttribute("width", "100%");
        svg.setAttribute("height", "100%");
        // Stretch to fill the container. The container is position:absolute
        // inset:0 over the canvas — match the canvas pixel dims for the SVG
        // viewBox so polygons read at intended pixel positions without scaling.
        let polyCount = 0;
        groupCentroids.forEach((entry, parentId) => {
            if (entry.points.length < 2)
                return;
            const hull = entry.points.length >= 3 ? convexHull(entry.points) : entry.points;
            if (hull.length < 2)
                return;
            const pointsAttr = hull.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
            const poly = document.createElementNS(svgNs, "polygon"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            poly.setAttribute("points", pointsAttr);
            poly.setAttribute("fill", "none");
            poly.setAttribute("stroke", entry.color);
            poly.setAttribute("stroke-width", "1.5");
            poly.setAttribute("stroke-dasharray", "5 3");
            poly.setAttribute("stroke-opacity", "0.55");
            poly.classList.add("editor-mcid-hull");
            poly.dataset.parentStructId = parentId;
            svg.appendChild(poly);
            polyCount++;
        });
        if (polyCount > 0) {
            container.appendChild(svg);
        }
    }
    return rendered;
}
/**
 * Clear all `.editor-mcid-bbox` markers and `.editor-mcid-hull-svg`
 * polygon containers from the supplied element. Used when toggling the
 * MCID overlay off without re-rendering the struct overlays.
 */
export function clearMcidOverlay(container) {
    if (!container)
        return;
    const markers = container.querySelectorAll(".editor-mcid-bbox");
    for (const m of markers)
        m.remove();
    const svgs = container.querySelectorAll(".editor-mcid-hull-svg");
    for (const s of svgs)
        s.remove();
}
// ─── Helpers ────────────────────────────────────────────────────────────────
/**
 * Convert a hex color (#rrggbb) to rgba(r, g, b, a). Supports the small set
 * of color literals this module emits; falls back to the input string for
 * unknown shapes (lets a future palette use rgb(...) without breaking).
 */
function withAlpha(hex, alpha) {
    if (typeof hex !== "string")
        return hex;
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m)
        return hex;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
